import { useMemo } from 'react';
import {
  ClipboardList,
  PhoneOutgoing,
  PhoneIncoming,
  Loader2,
  Ban,
  PhoneMissed,
  RefreshCw,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { CampaignRow } from '../types';

/**
 * Aggregate stat tiles shown above the campaign tables. Mirrors the reference
 * design: each tile has an icon block, the metric label, a small percentage
 * badge in the corner, and the count underneath.
 *
 * Percentages are taken against `Total Numbers` so the user can eyeball
 * connect/DND/pending ratios at a glance.
 */
interface Props {
  rows: CampaignRow[];
  /** Hide retry-related tiles when no row in the list carries retry data. */
  showRetry?: boolean;
}

type Tone =
  | 'slate'
  | 'green'
  | 'emerald'
  | 'purple'
  | 'red'
  | 'orange'
  | 'rose'
  | 'blue';

interface Tile {
  key: string;
  label: string;
  value: number;
  icon: LucideIcon;
  tone: Tone;
  /** Render a small % badge in the tile header (against totalNumbers). */
  pct?: boolean;
}

const ICON_BG: Record<Tone, string> = {
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  green: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  purple: 'bg-violet-100 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
  red: 'bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-950/40 dark:text-orange-400',
  rose: 'bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
  blue: 'bg-sky-100 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400',
};

/** Badge color reflects whether the metric is "good" (green) or "concern" (red). */
const BADGE_CLASSES = {
  good: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  bad: 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
  neutral:
    'bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
} as const;

const BADGE_TONE: Record<Tone, keyof typeof BADGE_CLASSES> = {
  slate: 'neutral',
  green: 'good',
  emerald: 'good',
  purple: 'neutral',
  red: 'bad',
  orange: 'bad',
  rose: 'bad',
  blue: 'bad',
};

export function CampaignSummaryBar({ rows, showRetry = false }: Props) {
  const stats = useMemo(() => {
    const acc = {
      totalNumbers: 0,
      callsDialed: 0,
      pendingCalls: 0,
      dndCount: 0,
      connectedCalls: 0,
      failedCalls: 0,
      retryCount: 0,
      totalReady: 0,
    };
    for (const c of rows) {
      const uploaded = Number(c.numbersUploaded ?? 0);
      const dialed = Number(c.numbersProcessed ?? 0);
      const dnd = Number(c.dndCount ?? 0);
      acc.totalNumbers += uploaded;
      acc.callsDialed += dialed;
      acc.pendingCalls += Math.max(0, uploaded - dnd - dialed);
      acc.dndCount += dnd;
      acc.connectedCalls += Number(c.callsConnected ?? 0);
      acc.failedCalls += Number(c.failedCount ?? 0);
      acc.retryCount += Number(c.retryCount ?? 0);
      if (Number(c.campaignStatus) === 0) acc.totalReady += uploaded;
    }
    return acc;
  }, [rows]);

  const pctOf = (v: number): string => {
    if (!stats.totalNumbers) return '0%';
    return `${((v / stats.totalNumbers) * 100).toFixed(1)}%`;
  };

  const tiles: Tile[] = [
    {
      key: 'campaigns',
      label: 'Total Campaigns',
      value: rows.length,
      icon: ClipboardList,
      tone: 'slate',
    },
    {
      key: 'dialed',
      label: 'Dialed',
      value: stats.callsDialed,
      icon: PhoneOutgoing,
      tone: 'green',
      pct: true,
    },
    {
      key: 'connected',
      label: 'Connected',
      value: stats.connectedCalls,
      icon: PhoneIncoming,
      tone: 'emerald',
      pct: true,
    },
    {
      key: 'pending',
      label: 'Pending',
      value: stats.pendingCalls,
      icon: Loader2,
      tone: 'purple',
      pct: true,
    },
    {
      key: 'dnd',
      label: 'DND',
      value: stats.dndCount,
      icon: Ban,
      tone: 'red',
      pct: true,
    },
    {
      key: 'failed',
      label: 'Failed Calls',
      value: stats.failedCalls,
      icon: PhoneMissed,
      tone: 'rose',
      pct: true,
    },
    ...(showRetry
      ? [
          {
            key: 'retry',
            label: 'Retry Count',
            value: stats.retryCount,
            icon: RefreshCw,
            tone: 'orange' as Tone,
            pct: true,
          },
        ]
      : []),
    {
      key: 'ready',
      label: 'Total Ready',
      value: stats.totalReady,
      icon: CheckCircle2,
      tone: 'blue',
      pct: true,
    },
  ];

  /*
   * Single-row strip on lg+: 8 tiles laid out across one line per the
   * reference design. Compact tiles (smaller icon, single-line label
   * with the % badge inline) keep each tile readable at ~12.5% width.
   * On smaller breakpoints we wrap to 4 → 2 → 1 column.
   */
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div
            key={t.key}
            className="flex items-center gap-2 rounded-xl border bg-card px-2.5 py-2"
          >
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                ICON_BG[t.tone],
              )}
            >
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <span className="truncate text-[10px] text-muted-foreground">
                  {t.label}
                </span>
                {t.pct && (
                  <span
                    className={cn(
                      'inline-flex shrink-0 rounded px-1 py-0 text-[9px] font-medium tabular-nums',
                      BADGE_CLASSES[BADGE_TONE[t.tone]],
                    )}
                  >
                    {pctOf(t.value)}
                  </span>
                )}
              </div>
              <div className="truncate text-sm font-semibold tabular-nums text-foreground">
                {t.value.toLocaleString()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
